// src/modules/app-views/toolbox/pages/Document/tabs/AiInfoTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Alert, Grid, Card, Descriptions, Tag, Typography } from 'antd';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';

const { Text, Paragraph } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AiInfoTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.md;
  const hasAiMeta = data?.aiCatalogMeta?.catalogedByAI === true;
  const aiResponse = data?.aiCatalogMeta?.aiResponse;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasAiMeta) {
    return (
      <div className="container" style={{ padding: isMobile ? 16 : 24 }}>
        <Alert
          type="info"
          showIcon
          title={translateX('tool_not_cataloged_by_ai')}
          description={translateX('tool_not_cataloged_by_ai_description')}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="aiInfo" isFetching={loading} data={data} />

      {/* AI CATALOG META SECTION */}
      <Card title={<div className="text-muted">{translateX('ai_catalog_info')}</div>}>
        <Descriptions column={isMobile ? 1 : 2} size="small" bordered>
          <Descriptions.Item label={translateX('cataloged_by_ai')}>
            <Tag color="green">{translateX('yes')}</Tag>
          </Descriptions.Item>
          {data?.aiCatalogMeta?.model && (
            <Descriptions.Item label={translateX('ai_model')}>
              <Text>{data.aiCatalogMeta.model}</Text>
            </Descriptions.Item>
          )}
          {data?.aiCatalogMeta?.catalogedAt && (
            <Descriptions.Item label={translateX('cataloged_at')}>
              <Text>{new Date(data.aiCatalogMeta.catalogedAt).toLocaleString()}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* AI RESPONSE DATA */}
      {aiResponse && (
        <Card title={<div className="text-muted">{translateX('ai_response_data')}</div>}>
          {aiResponse.description && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{translateX('description')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{aiResponse.description}</Paragraph>
            </div>
          )}

          {aiResponse.specifications && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{translateX('specifications')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{aiResponse.specifications}</Paragraph>
            </div>
          )}

          {aiResponse.searchTerms && aiResponse.searchTerms.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{translateX('search_terms')}</Text>
              <div style={{ marginTop: 4 }}>
                {aiResponse.searchTerms.map((term, idx) => (
                  <Tag key={idx} style={{ marginBottom: 4 }}>{term}</Tag>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AiInfoTab;
